/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useRef, useState } from 'react';

import { Box, Button, Flex, FlexProps, Heading, Stack, Text } from '@chakra-ui/react';
import MobileDetect from 'mobile-detect';

import { useInterviewAgent } from '@/store/InterviewAgent';

export default function DeviceTest({ ...props }: FlexProps) {
  const [isMediaAllowed, setIsMediaAllowed] = useState(false);
  const [isMediaSetting, setIsMediaSetting] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<
    'idle' | 'recording' | 'processing' | 'playback'
  >('idle');
  const { setRecordedStream, nextStep } = useInterviewAgent();

  const mimeType = useRef<string>('video/webm');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const md = new MobileDetect(navigator.userAgent);
    const isIphone = md.is('iPhone');

    // Check for supported MIME types with audio
    const supportedTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4;codecs=h264,aac',
      'video/mp4'
    ];

    let selectedType = 'video/webm';
    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        selectedType = type;
        break;
      }
    }

    // Fallback for iPhone
    if (isIphone && MediaRecorder.isTypeSupported('video/mp4')) {
      selectedType = 'video/mp4';
    }

    mimeType.current = selectedType;

    allowMediaSettings();
  }, []);

  const allowMediaSettings = async () => {
    const video = videoRef.current;
    if (!video) return;

    setIsMediaSetting(true);
    setPermissionError(null);

    try {
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      video.srcObject = localStream;
      video.muted = true; // Mute during live preview
      setRecordedStream(localStream);
      setIsMediaAllowed(true);
      setPermissionError(null);
      setRecordingStatus('idle');
    } catch (error: unknown) {
      console.error('Media access error:', error);

      let errorMessage =
        'Please confirm that your audio and video access is allowed and try again.';

      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage =
            'Camera and microphone access was denied. Please allow access in your browser settings and try again.';
        } else if (error.name === 'NotFoundError') {
          errorMessage =
            'No camera or microphone found. Please connect a camera and microphone and try again.';
        } else if (error.name === 'NotReadableError') {
          errorMessage =
            'Camera or microphone is already in use by another application. Please close other apps and try again.';
        } else if (error.name === 'OverconstrainedError') {
          errorMessage =
            'Camera or microphone does not meet the required specifications. Please try a different device.';
        }
      }

      setPermissionError(errorMessage);
      setIsMediaAllowed(false);
    }

    setIsMediaSetting(false);
  };

  const getStatusColor = () => {
    switch (recordingStatus) {
      case 'recording':
        return 'red.500';
      case 'processing':
        return 'yellow.500';
      case 'playback':
        return 'green.500';
      default:
        return 'gray.500';
    }
  };

  return (
    <Flex flexDir="column" h="100%" w="100%" align="center" justify="center" mt="32px" {...props}>
      {/* Header Section */}
      <Stack gap={4} mb={8} textAlign="center">
        <Heading as="h1" fontSize="32px" fontWeight={700} color="neutrals.900" lineHeight="1.2">
          Audio & video test
        </Heading>
        <Text fontSize="14px" color="neutrals.500" maxW="600px" lineHeight="1.5">
          Please check your camera and microphone to ensure they are working properly before
          starting the interview.
        </Text>
      </Stack>

      {/* Video Container */}
      <Box
        pos="relative"
        w="600px"
        h="375px"
        maxW="90vw"
        maxH="50vh"
        borderRadius="20px"
        overflow="hidden"
        boxShadow="0 10px 30px rgba(0, 0, 0, 0.2)"
        border="3px solid"
        borderColor={getStatusColor()}
        transition="all 0.3s ease"
        mb="24px"
      >
        <video
          ref={videoRef}
          id="rtc-video"
          autoPlay={true}
          muted={true}
          playsInline={true}
          style={{
            width: '100%',
            height: '100%',
            background: '#1a1a1a',
            transform: 'rotateY(180deg)',
            objectFit: 'cover'
          }}
        />

        {/* No Camera Message */}
        {!isMediaAllowed && (
          <Flex
            pos="absolute"
            inset="0"
            align="center"
            justify="center"
            bg="neutrals.900"
            color="white"
            textAlign="center"
            p="20px"
          >
            <Stack gap={3}>
              <Text fontSize="16px" fontWeight={500}>
                Camera Access Required
              </Text>
              <Text fontSize="14px" opacity={0.8}>
                Please allow camera and microphone access to continue
              </Text>
            </Stack>
          </Flex>
        )}
      </Box>

      {/* Instructions */}
      {!isMediaAllowed && (
        <Stack mb="24px" textAlign="center" maxW="600px">
          <Heading as="h3" color="neutrals.900">
            Enable Your Devices
          </Heading>

          <Text fontSize="14px" color="neutrals.500">
            We need access to your camera and microphone to conduct the interview. Please allow
            access when prompted.
          </Text>

          {/* Error Message */}
          {permissionError && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="12px"
              p="16px"
              w="100%"
            >
              <Text fontSize="14px" color="red.800" fontWeight={500}>
                ⚠️ {permissionError}
              </Text>
            </Box>
          )}
        </Stack>
      )}

      {/* Action Buttons */}
      <Flex gap={4} w="100%" maxW="400px" align="center" justify="center">
        <Button
          size="lg"
          fontSize="18px"
          fontWeight={600}
          w="320px"
          h="48px"
          color="white"
          borderRadius="8px"
          bgColor="primary.900"
          _hover={{
            bg: 'primary.700'
          }}
          loading={isMediaSetting}
          loadingText="Requesting Access"
          onClick={
            isMediaAllowed
              ? () => {
                  nextStep();
                }
              : allowMediaSettings
          }
          transition="all 0.2s ease"
        >
          {isMediaAllowed ? 'Next' : 'Allow Access'}
        </Button>
      </Flex>
    </Flex>
  );
}
